import { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { formatGetal, isTelling } from '../format';
import { BREEK_ONONDERBROKEN_NAAM, trendPlekken, uitlegVan } from '../leerkaart';
import { TrendContext, type TrendLezing } from '../trend-context';
import { heeftEigenNaam, veldnaamVan } from '../veldnaam';
import { werksoortLink } from '../werksoort-links';
import { ExamplePair } from './ExamplePair';
import { TrendStaven } from './TrendStaven';
import type { EigenHoofdveld, Veldnamen, WerksoortSleutel } from '../work-quality.api';

export type Kopniveau = 2 | 3 | 4 | 5 | 6;

export interface AandachtskaartProps {
  veld: EigenHoofdveld;
  /** De plek in de top 3, vanaf 1. */
  plek: number;
  /** De noemer van "bij X van Y": `inEenKeerGoed.aantalBeoordeeld` van de werksoort. */
  aantalBeoordeeld: number;
  soort: WerksoortSleutel;
  veldnamen?: Veldnamen;
  /** Het niveau van de kaartkop; de subkoppen staan één niveau dieper. */
  kopNiveau: 3 | 4 | 5;
}

const SUBKOP = 'text-[0.75rem] font-semibold uppercase tracking-[0.04em] text-[var(--foreground-muted)]';
const LINK = `rounded-[4px] text-[0.8125rem] text-[var(--secondary-text)] underline underline-offset-2 max-sm:inline-block max-sm:py-3 [@media(pointer:coarse)]:inline-block [@media(pointer:coarse)]:py-3 ${FOCUSRING}`;

/**
 * Het verloop over de laatste vier afgesloten weken voor dit veld, uit het overzicht van de pagina
 * (SPEC-meerweekenoverzicht-feedback CAP-6). Tijdens het laden niets; zonder overzicht een eigen
 * storingstekst en geen grafiek (AD-27). De kaart blijft staan.
 */
function Terugkomst({ lezing, soort, veld }: { lezing: TrendLezing | null; soort: WerksoortSleutel; veld: string }) {
  const { t } = useTranslation();
  if (lezing?.status === 'laden') return null;
  if (lezing === null || lezing.status === 'fout') {
    return (
      <p className="mt-1 text-[0.8125rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-trend-storing">
        {t('qa.eigen.trendStoring')}
      </p>
    );
  }
  return (
    <div className="mt-2">
      <TrendStaven plekken={trendPlekken(lezing.overzicht, lezing.weken, soort, veld)} />
    </div>
  );
}

function UitlegRegel({ Kop, kop, tekst, vlak = false }: { Kop: `h${Kopniveau}`; kop: string; tekst: string | null; vlak?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={vlak ? 'rounded-[8px] bg-[var(--secondary-light)] p-3' : ''}>
      <Kop className={SUBKOP}>{kop}</Kop>
      <p className={`mt-1 text-[0.875rem] [overflow-wrap:anywhere] ${tekst === null ? 'text-[var(--foreground-muted)]' : 'text-[var(--foreground)]'}`}>
        {tekst ?? t('qa.eigen.leerkaartUitlegVolgt')}
      </p>
    </div>
  );
}

/**
 * Eén Aandachtskaart: een van de drie velden die deze week het vaakst werden aangepast (M10, EXPERIENCE.md
 * Aandachtskaart). Wat gaat er meestal mis, waarom, zo moet het, één voorbeeld, en of het terugkomt.
 *
 * De kaart rekent niets: het aantal en de noemer komen uit het antwoord, de uitleg uit de maandagbatch. Een
 * ontbrekende uitlegregel zegt "uitleg volgt". Geen naam van een nakijker (M16), geen productnummer of GTIN,
 * en nooit het veldpad. Elke link opent in een nieuw tabblad en zegt dat.
 */
export function Aandachtskaart({ veld, plek, aantalBeoordeeld, soort, veldnamen, kopNiveau }: AandachtskaartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const kopId = useId();
  const Kop = `h${kopNiveau}` as const;
  const Subkop = `h${(kopNiveau + 1) as Kopniveau}` as const;
  // Zonder leesbare naam een vaste tekst, nooit het veldpad (ux-spec 3.3, criterium 34).
  const naam = heeftEigenNaam(veld.veld, veldnamen) ? veldnaamVan(veld.veld, veldnamen) : t('qa.eigen.veldnaamOnbekend');
  const uitleg = uitlegVan(veld);
  const trend = useContext(TrendContext);
  const link = werksoortLink(soort);
  const voorbeeld = Array.isArray(veld.voorbeelden) ? veld.voorbeelden[0] : undefined;

  return (
    <section
      aria-labelledby={kopId}
      className="flex min-w-0 flex-col gap-4 rounded-[12px] border border-[var(--border-soft)] bg-[var(--background)] p-5 shadow-[var(--card-shadow)]"
      data-testid={`werkkwaliteit-aandachtskaart-${veld.veld}`}
    >
      <div>
        <Kop
          className={`text-[0.9375rem] font-semibold text-[var(--foreground-heading)] ${naam.includes(' ') ? '' : BREEK_ONONDERBROKEN_NAAM}`}
          id={kopId}
        >
          {t('qa.rapportkaart.kaartKop', { plek: formatGetal(plek, locale), naam })}
        </Kop>
        <p className="mt-1 text-[0.75rem] text-[var(--foreground-muted)]">
          {isTelling(veld.aantalProductenMetCorrectie) && isTelling(aantalBeoordeeld)
            ? t('qa.eigen.leerkaartBij', { aantal: formatGetal(veld.aantalProductenMetCorrectie, locale), totaal: formatGetal(aantalBeoordeeld, locale) })
            : t('qa.eigen.nietGemeten')}
        </p>
      </div>

      <UitlegRegel Kop={Subkop} kop={t('qa.eigen.leerkaartFout')} tekst={uitleg.watGaatMis} />
      <UitlegRegel Kop={Subkop} kop={t('qa.eigen.leerkaartWaarom')} tekst={uitleg.waarom} />
      <UitlegRegel Kop={Subkop} kop={t('qa.eigen.leerkaartZoMoet')} tekst={uitleg.zoMoetHet} vlak />

      <div>
        <Subkop className={SUBKOP}>{t('qa.eigen.leerkaartVoorbeeld')}</Subkop>
        {voorbeeld ? (
          <div className="mt-2">
            <ExamplePair voorbeeld={voorbeeld} />
          </div>
        ) : (
          <p className="mt-1 text-[0.8125rem] text-[var(--foreground-muted)]">{t('qa.eigen.leerkaartGeenVoorbeeld')}</p>
        )}
      </div>

      <div className="rounded-[10px] border border-[var(--border)] bg-[var(--background-muted)] p-3" data-testid="werkkwaliteit-leerkaart-terugkomst">
        <Subkop className={SUBKOP}>{t('qa.eigen.leerkaartTerugkomst')}</Subkop>
        <p className="text-[0.75rem] text-[var(--foreground-muted)]">{t('qa.rapportkaart.laatsteWeken')}</p>
        <Terugkomst lezing={trend} soort={soort} veld={veld.veld} />
      </div>

      {link && (
        <p>
          <a className={LINK} href={link.href} rel="noopener noreferrer" target="_blank">
            {t(link.tekstSleutel)} <span className="sr-only">{t('qa.rapportkaart.nieuwTabblad')}</span>
          </a>
        </p>
      )}
    </section>
  );
}
